<script setup lang="ts">
import { computed, watch } from 'vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import type {
  StepDeliveryFormErrorManager,
  StepDeliveryFormErrorPath,
  StepDeliveryStepAnswerSelectionFormType,
} from '@/components/organisms/StepDeliveryForm/formSchema'

const props = defineProps<{
  stepIndex: number
  formErrorManager: StepDeliveryFormErrorManager
}>()

const model = defineModel<StepDeliveryStepAnswerSelectionFormType[]>({
  required: true,
})

// 編集画面を開いたときに DB に存在する選択肢に対する guid を追跡するためにマップを作成。
// フォームから選択肢を追加して保存された場合、その選択肢とサーバー側で発行される guid がこのマップに追加される。
const answerSelectionToGuidMap = new Map(
  model.value.map(ans => [ans.selection, ans.guid])
)

const selections = computed<string[]>({
  get: () => model.value.map(selection => selection.selection),
  set: newSelections => {
    // 選択肢の文言が DB のそれと一致するときにのみ guid を維持し、
    // 一致するものが無い場合、guid は空にして保存時にサーバー側で新規発行する。
    // 同じ文言の選択肢がある場合に guid は一つに統一されるが、分ける理由も無いので問題ない。
    model.value = [
      ...newSelections.map(s => {
        const guid = answerSelectionToGuidMap.get(s)
        return guid ? { guid, selection: s } : { guid: '', selection: s }
      }),
    ]
  },
})

watch(model, () => {
  // submit 後にサーバーで guid が再発行されたときに answerSelectionToGuidMap に反映する。
  // 編集前に存在した特定の文言に対する guid を追跡するために追加のみ行い削除はしない。
  // 選択肢の増減を続けた場合に answerSelectionToGuidMap は増大していくが、
  // 画面更新をすれば DB にある選択肢の数へとリフレッシュされるので問題ない。
  model.value.forEach(ans => {
    if (ans.selection) {
      answerSelectionToGuidMap.set(ans.selection, ans.guid)
    }
  })
})

const selectionFormError = computed<string | undefined>(() => {
  for (const index of model.value.keys()) {
    const errorMessage = props.formErrorManager.get(
      `steps.${props.stepIndex}.answerSelections.${index}.selection` as StepDeliveryFormErrorPath
    )
    if (errorMessage) {
      return errorMessage
    }
  }
  return undefined
})
</script>

<template>
  <div>
    <MessageAttachmentActions
      v-model="selections"
      :shouldShowTimeSlotPickerButton="false"
      :invalid="!!selectionFormError"
      isDeleteAllSelectionsAllowed
    >
      <template #optionalMessage>
        <p :class="$style.error">{{ selectionFormError }}</p>
      </template>
    </MessageAttachmentActions>
  </div>
</template>

<style lang="scss" module>
.error {
  color: $color-caution-accent;
}
</style>
